import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import Hours from '../../../smoothr-web-app-core/models/Hours';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { AppComponent } from '../../app.component';
import { TranslateService } from '@ngx-translate/core';
import { TimeUtils } from '../../../smoothr-web-app-core/utils/time-utils';
import { environment } from 'src/environments/environment';
import { Api } from '../../../smoothr-web-app-core/api/api';
import { ShortInfoModalComponent } from '../short-info-modal/short-info-modal.component';
import { numberToCurrency } from 'src/smoothr-web-app-core/utils/utils';
import { NavigationService } from 'src/app/services/navigation.service';
import moment from 'moment';

@Component({
	selector: 'app-working-hours-modal',
	templateUrl: './working-hours-modal.component.html',
	styleUrls: ['working-hours-modal.component.scss'],
})
export class WorkingHoursModalComponent implements OnInit {
	venue: Venue;
	type: PreorderType;
	pt = PreorderType;
	environment = environment;
	numberToCurrency = numberToCurrency;

	openingHoursMapped: [string, string][] = [];
	deliveryHoursMapped: [string, string][] = [];

	// tslint:disable-next-line:variable-name
	private _longDays = [
		this.translate.instant('weekday.monday'),
		this.translate.instant('weekday.tuesday'),
		this.translate.instant('weekday.wednesday'),
		this.translate.instant('weekday.thursday'),
		this.translate.instant('weekday.friday'),
		this.translate.instant('weekday.saturday'),
		this.translate.instant('weekday.sunday'),
	];

	constructor(private modalCtrl: ModalController, private translate: TranslateService, private navService: NavigationService) {}

	// tslint:disable-next-line:variable-name
	private _days = [
		this.translate.instant('weekday.monday_short'),
		this.translate.instant('weekday.tuesday_short'),
		this.translate.instant('weekday.wednesday_short'),
		this.translate.instant('weekday.thursday_short'),
		this.translate.instant('weekday.friday_short'),
		this.translate.instant('weekday.saturday_short'),
		this.translate.instant('weekday.sunday_short'),
	];

	get days() {
		// return AppComponent.largeScreen ? this._longDays : this._days;
		return this._days;
	}

	ngOnInit() {
		if (this.venue) {
			this.openingHoursMapped = this.mappedDays(this.venue.openingHours);
			this.deliveryHoursMapped = this.mappedDays(this.venue.deliveryHours);

			// Apply special opening hours if they exist for today
			this.applySpecialHoursForToday();
		}
	}

	applySpecialHoursForToday() {
		try {
			const now = moment();
			const todayWeekday = now.weekday() === 0 ? 6 : now.weekday() - 1; // Convert moment weekday (0=Sunday) to app weekday (0=Monday)
			const closedText = this.translate.instant('venue_suggestion.close');

			// Apply special opening hours for restaurant
			if (this.venue?.specialOpeningHours?.length > 0) {
				const foundSpecialDate = this.venue.specialOpeningHours.find(it => moment(it.date).isSame(now, 'date'));
				if (foundSpecialDate && !foundSpecialDate.isClosed) {
					// Replace today's hours with special hours
					const specialHours = TimeUtils.transformSpecialHours(foundSpecialDate);

					// Update opening hours for today
					this.openingHoursMapped = this.openingHoursMapped.map(([days, time]) => {
						if (days.includes(this.days[todayWeekday])) {
							return [days, `${specialHours.openedAt.format('HH:mm')} - ${specialHours.closedAt.format('HH:mm')} ${this.translate.instant('working_hours_modal.hours')}`];
						}
						return [days, time];
					});
				} else if (foundSpecialDate && foundSpecialDate.isClosed) {
					// Mark as closed for today if special closing hours
					this.openingHoursMapped = this.openingHoursMapped.map(([days, time]) => {
						if (days.includes(this.days[todayWeekday])) {
							return [days, closedText];
						}
						return [days, time];
					});
				}
			}

			// Apply special opening hours for delivery (separate from restaurant)
			if (this.venue?.specialDeliveryHours?.length > 0) {
				const foundSpecialDeliveryDate = this.venue.specialDeliveryHours.find(it => moment(it.date).isSame(now, 'date'));
				if (foundSpecialDeliveryDate && !foundSpecialDeliveryDate.isClosed) {
					// Replace today's delivery hours with special delivery hours
					const specialDeliveryHours = TimeUtils.transformSpecialHours(foundSpecialDeliveryDate);

					// Update delivery hours for today
					this.deliveryHoursMapped = this.deliveryHoursMapped.map(([days, time]) => {
						if (days.includes(this.days[todayWeekday])) {
							return [days, `${specialDeliveryHours.openedAt.format('HH:mm')} - ${specialDeliveryHours.closedAt.format('HH:mm')} ${this.translate.instant('working_hours_modal.hours')}`];
						}
						return [days, time];
					});
				} else if (foundSpecialDeliveryDate && foundSpecialDeliveryDate.isClosed) {
					// Mark delivery as closed for today if special delivery closing hours
					this.deliveryHoursMapped = this.deliveryHoursMapped.map(([days, time]) => {
						if (days.includes(this.days[todayWeekday])) {
							return [days, closedText];
						}
						return [days, time];
					});
				}
			} else if (this.venue?.specialOpeningHours?.length > 0) {
				// Fallback: If no special delivery hours exist, use special opening hours for delivery
				const foundSpecialDate = this.venue.specialOpeningHours.find(it => moment(it.date).isSame(now, 'date'));
				if (foundSpecialDate && !foundSpecialDate.isClosed) {
					const specialHours = TimeUtils.transformSpecialHours(foundSpecialDate);

					this.deliveryHoursMapped = this.deliveryHoursMapped.map(([days, time]) => {
						if (days.includes(this.days[todayWeekday])) {
							return [days, `${specialHours.openedAt.format('HH:mm')} - ${specialHours.closedAt.format('HH:mm')} ${this.translate.instant('working_hours_modal.hours')}`];
						}
						return [days, time];
					});
				} else if (foundSpecialDate && foundSpecialDate.isClosed) {
					this.deliveryHoursMapped = this.deliveryHoursMapped.map(([days, time]) => {
						if (days.includes(this.days[todayWeekday])) {
							return [days, closedText];
						}
						return [days, time];
					});
				}
			}
		} catch (e) {
			console.error('Error applying special hours:', e);
		}
	}

	static async show(modalCtrl: ModalController, venue: Venue, type: PreorderType) {
		// Fetch full venue data if invoiceReceiver is missing.
		// Venues loaded from the map page use a lightweight query (mapsPoiVenues)
		// that does not include invoiceReceiver, causing the venue name h2 to be blank.
		if (!venue.invoiceReceiver) {
			try {
				const fullVenue = (await Api.getLazyVenue(venue._id)).data;
				if (fullVenue?.invoiceReceiver) {
					venue.invoiceReceiver = fullVenue.invoiceReceiver;
				}
			} catch (e) {
				console.error('Error while fetching full venue data for working hours modal:', e);
			}
		}
		try {
			venue.legal = (await Api.getLegal(venue._id)).data;
			console.log({
				venue: venue.name,
				legal: venue.legal,
				id: venue._id,
			});
		} catch (e) {
			console.error('Error while getting legal information');
			console.error(e);
		}
		const modal = await modalCtrl.create({
			component: WorkingHoursModalComponent,
			cssClass: 'working-hours-modal',
			componentProps: {
				venue,
				type,
			},
			showBackdrop: true,
			backdropDismiss: true,
		});
		await modal.present();
	}

	async openHtml(html: string) {
		await ShortInfoModalComponent.show(this.modalCtrl, {
			de: html,
		});
	}

	dayText(schedule: Hours[], index: number): string[] {
		const res = [];

		const allConnected = schedule.map(hour => hour.connectedWithPrev).indexOf(false) < 0;
		for (const hour of schedule) {
			if (hour.connectedWithPrev && !allConnected) {
				continue;
			}
			if (hour.weekday === index) {
				const hours = TimeUtils.hoursToSchedule(hour);
				res.push(
					hours.openedAt.format('HH:mm') +
						' - ' +
						hours.closedAt.format('HH:mm') +
						` ${this.translate.instant('working_hours_modal.hours')}`
				);
			}
		}
		if (res.length !== 0) {
			return res;
		}
		return [this.translate.instant('venue_suggestion.close')];
	}

	mappedDays(days: Hours[]): [string, string][] {
		const newResp: {
			[key: string]: string[];
		} = {};
		const transformedNewResp: {
			[key: string]: string;
		} = {};
		const closedKey = this.translate.instant('venue_suggestion.close');

		console.log(this.days);

		for (let i = 0; i < this.days.length; i++) {
			const day = days.find(d => d.weekday === i);
			if (day) {
				const from = day.openedAt;
				const to = day.closedAt;
				const key = `${from} - ${to}`;
				if (key in newResp) {
					newResp[key].push(this.days[i]);
				} else {
					newResp[key] = [this.days[i]];
				}
			} else {
				if (closedKey in newResp) {
					newResp[closedKey].push(this.days[i]);
				} else {
					newResp[closedKey] = [this.days[i]];
				}
			}
		}

		for (let key in newResp) {
			transformedNewResp[key] = this.transformDaysOfWeek(newResp[key]);
		}

		return Object.entries(transformedNewResp);
	}

	dismiss() {
		this.modalCtrl.dismiss();
	}

	transformDaysOfWeek(days: string[]) {
		if (days.length === 2) {
			return days.join(' & ');
		} else {
			let lastDay = days.pop();
			let joinValue = days.join(', ');
			let length = days.length;

			return joinValue + (length > 1 ? ' & ' : '') + lastDay;
		}
	}

	async toPrivacy() {
		await this.modalCtrl.dismiss();
		window.open('/assets/datenschutz.pdf', '_blank');
	}
	async toAGB() {
		await this.modalCtrl.dismiss();
		window.open('/assets/agb.pdf', '_blank');
	}
	async toImpressum() {
		await this.modalCtrl.dismiss();
		window.open('/assets/Impressum_LinzPro.pdf', '_blank');
	}
}
